/* =========================================================================
   Checo Pérez × MAJA® — Landing SP26: coreografía de scroll
   Requiere: GSAP 3.12.5 + ScrollTrigger (se cargan solos por CDN) y Lenis
   (opcional, scroll suave). Todo el acceso al DOM es por atributos data-*,
   así que el mismo archivo sirve en cualquier plantilla que use el markup.
   Configuración: window.CSP26_CONFIG = { ... } antes de cargar este archivo.
   ========================================================================= */
class CSP26Landing {
  constructor(props) {
    this.props = Object.assign({
      accent: '#C9D629',      // verde MAJA
      smoothScroll: true,     // Lenis
      scrollLength: 1,        // multiplicador de la longitud de cada pin
      showCues: true,         // indicadores "Sigue bajando"
      forceAutoReveal: false, // revela el hero sin mover el cursor
      parallax: true          // parallax de los fondos cuadriculados
    }, props || {});
  }

  start() {
    this.tweens = [];
    this.narrow = window.innerWidth <= 1060;
    this.initMotion();
  }


  destroy() {
    this.dead = true;
    if (this.rafFn && window.gsap) window.gsap.ticker.remove(this.rafFn);
    if (this.heroStop) this.heroStop();
    if (this.sceneTick && window.gsap) window.gsap.ticker.remove(this.sceneTick);
    if (this.onSceneResize) window.removeEventListener('resize', this.onSceneResize);
    if (this.syncEtapaOverlap && window.ScrollTrigger) window.ScrollTrigger.removeEventListener('refreshInit', this.syncEtapaOverlap);
    if (this.lenis) this.lenis.destroy();
    if (this.lensCleanup) this.lensCleanup();
    if (this.ctx) this.ctx.revert();
  }

  loadScript(src) {
    return new Promise((resolve, reject) => {
      const s = document.createElement('script');
      s.src = src; s.async = false;
      s.onload = resolve; s.onerror = reject;
      document.head.appendChild(s);
    });
  }

  async initMotion() {
    try {
      if (!window.gsap) await this.loadScript('https://cdnjs.cloudflare.com/ajax/libs/gsap/3.12.5/gsap.min.js');
      if (!window.ScrollTrigger) await this.loadScript('https://cdnjs.cloudflare.com/ajax/libs/gsap/3.12.5/ScrollTrigger.min.js');
    } catch (err) { return; }
    if (!window.gsap || !window.ScrollTrigger || this.dead) return;

    const gsap = window.gsap;
    const ST = window.ScrollTrigger;
    gsap.registerPlugin(ST);

    if (this.props.smoothScroll !== false) {
      try {
        if (!window.Lenis) await this.loadScript('https://cdn.jsdelivr.net/npm/lenis@1.1.14/dist/lenis.min.js');
        const L = window.Lenis && (window.Lenis.default || window.Lenis);
        if (L && !this.dead) {
          this.lenis = new L({ duration: 1.15, smoothWheel: true, wheelMultiplier: 0.95, touchMultiplier: 1.6 });
          window.__lenis = this.lenis;
          this.lenis.on('scroll', ST.update);
          this.rafFn = (t) => this.lenis.raf(t * 1000);
          gsap.ticker.add(this.rafFn);
          gsap.ticker.lagSmoothing(0);
        }
      } catch (err) { /* scroll nativo */ }
    }
    if (this.dead) return;

    this.ctx = gsap.context(() => {
      this.buildHero();
      this.buildApertura();
      this.buildIntro();
      this.buildEtapa();
      this.buildEstandar();
      this.buildDecisiones();
      this.syncLayersBar();
      this.syncTrackBar('[data-kids-grid]', '[data-kl-thumb]');
      this.syncTrackBar('[data-caps-track]', '[data-caps-thumb]');
      this.buildSecuencia();
      this.buildVuelta();
      this.buildExigencia();
      this.buildKids();
      this.buildKidsLinea();
      this.buildPasion();
      this.buildExtra();
      this.buildCierre();
      this.buildTicker(ST);
      this.buildNav(ST);
    });
    ST.refresh();

    // Enlaces externos (p. ej. "Conocer la colección" del home) llegan con #ver-productos: ese hash
    // no coincide con ningún id, así el navegador no hace su propio salto, y se aterriza igual que
    // el botón "Ver productos". ScrollTrigger se vuelve a medir solo durante la carga (imágenes,
    // fuentes) y al hacerlo restaura una posición vieja, así que se re-aterriza tras cada refresh
    // de los primeros segundos, salvo que la persona ya se haya movido.
    if ((location.hash === '#ver-productos' || location.hash === '#productos') && this.goTo) {
      if ('scrollRestoration' in history) history.scrollRestoration = 'manual';
      let moved = false;
      const stop = () => { moved = true; };
      ['wheel', 'touchstart', 'keydown', 'pointerdown'].forEach((e) => window.addEventListener(e, stop, { once: true, passive: true }));
      const land = () => { if (!this.dead && !moved) this.goTo('vuelta'); };
      ST.addEventListener('refresh', land);
      setTimeout(() => ST.removeEventListener('refresh', land), 5000);
      land();
    }
  }

  len(base) {
    const k = this.props.scrollLength || 1;
    return Math.round(base * k) + '%';
  }

  cue(sec) {
    const c = sec.querySelector('[data-cue]');
    if (!c) return null;
    if (this.props.showCues === false) { window.gsap.set(c, { display: 'none' }); return null; }
    return c;
  }

  buildHero() {
    const gsap = window.gsap;
    const sec = document.querySelector('[data-ch="hero"]');
    if (!sec) return;
    const lines = gsap.utils.toArray(sec.querySelectorAll('[data-hero-line]'));
    const items = gsap.utils.toArray(sec.querySelectorAll('[data-hero-item]'));
    const photo = sec.querySelector('[data-hero-photo]');
    const cue = this.cue(sec);

    // las líneas entran lanzadas desde la izquierda, como si pasaran a toda velocidad
    gsap.set(lines, { xPercent: -108, skewX: 9, opacity: 0 });
    gsap.set(items, { opacity: 0, y: 18 });
    if (photo) gsap.set(photo, { opacity: 0, scale: 1.04 });
    if (cue) gsap.set(cue, { opacity: 0 });

    const tl = gsap.timeline({ delay: 0.12 });
    tl.to(items, { opacity: 1, y: 0, ease: 'power2.out', duration: 0.6 }, 0);
    if (photo) tl.to(photo, { opacity: 1, scale: 1, ease: 'power2.out', duration: 1.2 }, 0.1);
    tl.to(lines, { xPercent: 0, skewX: 0, opacity: 1, ease: 'power4.out', stagger: 0.085, duration: 0.85 }, 0.22);
    if (cue) tl.to(cue, { opacity: 1, duration: 0.6 }, 1.1);

    // la foto outdoor se descubre bajo el cursor
    const reveal = sec.querySelector('[data-hero-reveal]');
    if (reveal) {
      const pos = { x: 0, y: 0, d: 0 };
      const ring = sec.querySelector('[data-hero-ring]');

      // chorro tipo DRS: filamentos alargados orientados al flujo, pintados en canvas
      const ctx = reveal.getContext('2d');
      const mqTall = window.matchMedia('(max-width:1000px) and (orientation:portrait)');
      const mqLand = window.matchMedia('(max-width:1000px) and (orientation:landscape)');
      let tall = mqTall.matches;
      const air = new Image();
      let airOK = false;
      let plate = null;
      air.onload = () => { airOK = true; plate = null; };
      const R = (window.__resources || {});
      const srcFor = () => (tall
        ? (R.heroOutdoorTall || 'assets/hero-outdoor-mobile.webp?v=3')
        : (R.heroOutdoorWide || 'assets/hero-outdoor.webp?v=3'));
      air.src = srcFor();
      const dpr = Math.min(2, window.devicePixelRatio || 1);
      const fit = () => {
        if (mqTall.matches !== tall) {
          tall = mqTall.matches;
          airOK = false;
          air.src = srcFor();
        }
        plate = null;
        const r = sec.getBoundingClientRect();
        reveal.width = Math.round(r.width * dpr);
        reveal.height = Math.round(r.height * dpr);
      };
      fit();
      window.addEventListener('resize', fit);

      const P = 40;
      const parts = [];
      for (let i = 0; i < P; i++) parts.push({ x: 0, y: 0, vx: 0, vy: 0, ang: 0, l0: 0, lg: 0, w0: 0, wg: 0, life: 0, seed: 0 });
      let emitVX = 0, emitVY = 0;
      let dirX = 1, dirY = 0, spd = 0;
      const lastEmit = { x: 0, y: 0 };
      const prev = { x: 0, y: 0, t: performance.now() };
      let locked = false;

      const shed = (x, y) => {
        let p = parts[0];
        for (let i = 1; i < P; i++) if (parts[i].life < p.life) p = parts[i];
        const out = Math.random() * 6.2832;
        const v = 2.2 + Math.min(26, spd) * (0.14 + Math.random() * 0.2) + Math.random() * 2.4;
        p.x = x + Math.cos(out) * pos.d * 0.04;
        p.y = y + Math.sin(out) * pos.d * 0.04;
        p.vx = Math.cos(out) * v + emitVX * 0.1;
        p.vy = Math.sin(out) * v + emitVY * 0.1;
        p.ang = out;
        p.l0 = pos.d * 0.1;
        p.lg = pos.d * (0.16 + Math.random() * 0.12);
        p.w0 = pos.d * 0.03;
        p.wg = pos.d * (0.13 + Math.random() * 0.08);
        p.seed = Math.random() * 100;
        p.life = 1;
      };

      // cada elemento es un geoide irregular, no una elipse
      // ctx.filter no basta con existir: Safari lo omite o lo ignora, así que se prueba pintando un
      // cuadro con la misma transformación que usa streak() y se mira que el blur quede en píxeles
      // reales (sin escalar por la transformación). Si falla, se usa el degradado gaussiano.
      const canBlur = (() => {
        try {
          const t = document.createElement('canvas');
          t.width = t.height = 64;
          const c = t.getContext('2d', { willReadFrequently: true });
          if (typeof c.filter !== 'string') return false;
          c.translate(32, 32);
          c.scale(4, 4);
          c.filter = 'blur(4px)';
          c.fillStyle = '#000';
          c.fillRect(-1, -1, 2, 2);
          const px = (x) => c.getImageData(x, 32, 1, 1).data[3];
          return px(32) > 60 && px(39) > 8 && px(60) < 8;
        } catch (e) { return false; }
      })();
      // sin filtro: mancha gaussiana equivalente (la geometría del geoide queda bajo el blur)
      const glow = ctx.createRadialGradient(0, 0, 0, 0, 0, 1);
      for (let i = 0; i <= 8; i++) {
        const t = i / 8;
        glow.addColorStop(t, 'rgba(0,0,0,' + (i === 8 ? 0 : Math.exp(-4.5 * t * t)).toFixed(4) + ')');
      }
      let soft = false;
      const NV = 13;
      const streak = (x, y, ang, len, wid, a, seed) => {
        const L = Math.max(2, len) * dpr;
        const W = Math.max(2, wid) * dpr;
        const sd = seed || 0;
        if (!canBlur) {
          // disco (L, W) difuminado con σ = 0.95·min(L, W) ≈ gaussiana elíptica de σ² = semieje²/4 + σ²
          const s = Math.min(L, W) * 0.95;
          const sx = Math.sqrt(L * L / 4 + s * s), sy = Math.sqrt(W * W / 4 + s * s);
          ctx.save();
          ctx.translate(x * dpr, y * dpr);
          ctx.rotate(ang);
          ctx.scale(sx * 3, sy * 3);
          ctx.globalAlpha = +a * (1 - Math.exp(-(L * W) / (2 * s * s)));
          ctx.fillStyle = glow;
          ctx.fillRect(-1, -1, 2, 2);
          ctx.restore();
          return;
        }
        const rad = (i) => 1
          + 0.15 * Math.sin(sd * 1.27 + i * 0.86)
          + 0.08 * Math.sin(sd * 0.71 + i * 1.42);
        const pt = (i) => {
          const t = (i % NV) / NV * 6.2832;
          const r = rad(i % NV);
          return [Math.cos(t) * r, Math.sin(t) * r];
        };
        ctx.save();
        ctx.translate(x * dpr, y * dpr);
        ctx.rotate(ang);
        ctx.scale(L, W);
        ctx.filter = 'blur(' + (Math.min(L, W) * 0.95).toFixed(1) + 'px)';
        ctx.beginPath();
        let p0 = pt(0), p1 = pt(1);
        ctx.moveTo((p0[0] + p1[0]) / 2, (p0[1] + p1[1]) / 2);
        for (let i = 1; i <= NV; i++) {
          const c = pt(i), n = pt(i + 1);
          ctx.quadraticCurveTo(c[0], c[1], (c[0] + n[0]) / 2, (c[1] + n[1]) / 2);
        }
        ctx.closePath();
        ctx.fillStyle = 'rgba(0,0,0,' + a + ')';
        ctx.fill();
        ctx.filter = 'none';
        ctx.restore();
      };

      const paint = () => {
        if (!airOK) return;
        const cw = reveal.width, ch = reveal.height;
        // la foto se compone una sola vez, con el cielo prolongado hasta arriba
        if (!plate || plate.width !== cw || plate.height !== ch) {
          plate = document.createElement('canvas');
          plate.width = cw;
          plate.height = ch;
          const pc = plate.getContext('2d');
          const sc = tall
            ? Math.min(cw / air.width, ch / air.height)
            : Math.max(cw / air.width, ch / air.height);
          const dw = air.width * sc, dh = air.height * sc;
          const px = (!tall && mqLand.matches) ? 0.78 : 0.5;
          const dx = (cw - dw) * px;
          const dy = tall ? ch - dh : (ch - dh) / 2;
          if (dy > 0) pc.drawImage(air, 0, 0, air.width, 4, dx, 0, dw, dy + 1);
          if (dy + dh < ch) pc.drawImage(air, 0, air.height - 4, air.width, 4, dx, dy + dh - 1, dw, ch - dy - dh + 1);
          if (dx > 0) {
            pc.drawImage(air, 0, 0, 4, air.height, 0, dy, dx + 1, dh);
            pc.drawImage(air, air.width - 4, 0, 4, air.height, dx + dw - 1, dy, cw - dx - dw + 1, dh);
            if (dy > 0) {
              pc.drawImage(air, 0, 0, 4, 4, 0, 0, dx + 1, dy + 1);
              pc.drawImage(air, air.width - 4, 0, 4, 4, dx + dw - 1, 0, cw - dx - dw + 1, dy + 1);
            }
          }
          pc.drawImage(air, dx, dy, dw, dh);
        }
        ctx.globalCompositeOperation = 'source-in';
        ctx.drawImage(plate, 0, 0);
        ctx.globalCompositeOperation = 'source-atop';
        const lg = ctx.createLinearGradient(0, 0, cw, 0);
        lg.addColorStop(0, 'rgba(0,0,0,.82)');
        lg.addColorStop(0.38, 'rgba(0,0,0,.6)');
        lg.addColorStop(0.72, 'rgba(0,0,0,.3)');
        lg.addColorStop(0.9, 'rgba(0,0,0,0)');
        ctx.fillStyle = lg;
        ctx.fillRect(0, 0, cw, ch);
        ctx.globalCompositeOperation = 'source-over';
      };

      let inView = true;
      let running = false;
      let autoRun = false;
      const stop = () => {
        if (!running) return;
        running = false;
        gsap.ticker.remove(render);
        ctx.globalCompositeOperation = 'source-over';
        ctx.clearRect(0, 0, reveal.width, reveal.height);
      };
      const start = () => {
        if (running || !inView) return;
        running = true;
        gsap.ticker.add(render);
      };
      window.ScrollTrigger.create({
        trigger: sec, start: 'top bottom', end: 'bottom top',
        onToggle: (self) => { inView = self.isActive; if (!inView) stop(); }
      });
      this.heroStop = stop;

      const render = (time, dtms) => {
        const dt = Math.min(40, dtms || 16) / 1000;
        const f = dt * 60;
        ctx.globalCompositeOperation = 'source-over';
        ctx.clearRect(0, 0, reveal.width, reveal.height);

        if (locked) {
          ctx.fillStyle = 'rgba(0,0,0,1)';
          ctx.fillRect(0, 0, reveal.width, reveal.height);
          paint();
          return;
        }

        // la boca del chorro: una lente alargada en la dirección de avance
        const da = Math.atan2(dirY, dirX);
        const st = Math.min(1, spd / 24);
        const wob = time * 0.0007;
        const kL = 0.2, kW = 0.15;
        streak(pos.x, pos.y, da, pos.d * (kL + st * 0.08), pos.d * (kW + st * 0.04), '0.98', wob);

        let alive = 0;
        for (let i = 0; i < P; i++) {
          const p = parts[i];
          if (p.life <= 0) continue;
          alive++;
          p.life -= dt / 1.25;
          p.vx *= 0.972;
          p.vy *= 0.972;
          p.x += p.vx * f;
          p.y += p.vy * f;
          const lf = Math.max(0, Math.min(1, p.life));
          const k = 1 - lf;
          const len = p.l0 + (p.lg - p.l0) * k;
          const wid = p.w0 + (p.wg - p.w0) * k;
          const a = (Math.pow(lf, 1.45) * 0.78).toFixed(3);
          if (+a < 0.02) continue;
          p.ang = Math.atan2(p.vy, p.vx);
          streak(p.x, p.y, p.ang, len, wid, a, p.seed);
        }

        paint();
        if (!alive && pos.d < 1 && !autoRun) stop();

        if (ring) {
          const r = pos.d * 0.42;
          ring.style.width = r + 'px';
          ring.style.height = r + 'px';
          ring.style.transform = 'translate(' + (pos.x - r / 2) + 'px,' + (pos.y - r / 2) + 'px)';
        }
      };
      const qx = gsap.quickTo(pos, 'x', { duration: 0.07, ease: 'power1.out' });
      const qy = gsap.quickTo(pos, 'y', { duration: 0.07, ease: 'power1.out' });
      const size = () => Math.min(window.innerWidth * 0.95, window.innerHeight * 1.7, 1800);

      const hoverOK = window.matchMedia('(hover:hover)').matches;
      const forceAuto = () => this.props.forceAutoReveal === true || /[?&]auto=1/.test(location.search);
      const hoverActive = () => hoverOK && !forceAuto();
      const runAuto = () => {
        // sin cursor: el revelado hace un recorrido automático al cargar
        const r = sec.getBoundingClientRect();
        locked = false;
        autoRun = true;
        soft = true;
        parts.forEach((p) => { p.life = 0; });
        pos.x = r.width * 0.5; pos.y = r.height * 0.1;
        lastEmit.x = pos.x; lastEmit.y = pos.y;
        const trace = () => {
          const dx = pos.x - lastEmit.x, dy = pos.y - lastEmit.y;
          if (Math.hypot(dx, dy) > 12) {
            emitVX = dx; emitVY = dy;
            const m = Math.hypot(dx, dy) || 1;
            dirX = dx / m; dirY = dy / m;
            spd = m * 0.8;
            shed(pos.x, pos.y);
            lastEmit.x = pos.x; lastEmit.y = pos.y;
          }
        };
        start();
        gsap.timeline({ delay: 0.35, onComplete: () => {
          gsap.to(reveal, { opacity: 0, duration: 1.3 });
          gsap.to(pos, { d: 0, duration: 1.4, onComplete: () => { autoRun = false; soft = false; } });
        } })
          .to(reveal, { opacity: 1, duration: 0.5 }, 0)
          .to(pos, { d: size() * 2.4, duration: 1.3, ease: 'power2.out' }, 0)
          .to(pos, { x: r.width * 0.54, y: r.height * 0.4, duration: 2.4, ease: 'sine.inOut', onUpdate: trace }, 0.2)
          .to(pos, { x: r.width * 0.5, y: r.height * 0.78, duration: 2.6, ease: 'sine.inOut', onUpdate: trace })
          // se queda un momento abajo: el marco amarillo cede y aparece la placa de aluminio
          .to(pos, { x: r.width * 0.5, y: r.height * 0.9, duration: 2.6, ease: 'sine.inOut', onUpdate: trace })
          .to(pos, { x: r.width * 0.52, y: r.height * 0.86, duration: 2.2, ease: 'sine.inOut', onUpdate: trace });
      };
      this.heroAuto = runAuto;
      window.__heroAuto = runAuto;
      if (!hoverOK || forceAuto()) runAuto();

      sec.addEventListener('pointermove', (e) => {
        if (locked || !hoverActive()) return;
        const b = sec.getBoundingClientRect();
        const x = e.clientX - b.left, y = e.clientY - b.top;
        const now = performance.now();
        const span = Math.max(8, now - prev.t);
        emitVX = ((x - prev.x) / span) * 16;
        emitVY = ((y - prev.y) / span) * 16;
        prev.x = x; prev.y = y; prev.t = now;
        const mag = Math.hypot(emitVX, emitVY);
        if (mag > 0.4) {
          dirX += (emitVX / mag - dirX) * 0.25;
          dirY += (emitVY / mag - dirY) * 0.25;
          const dm = Math.hypot(dirX, dirY) || 1;
          dirX /= dm; dirY /= dm;
        }
        spd += (mag - spd) * 0.22;
        if (pos.d === 0) { pos.x = x; pos.y = y; lastEmit.x = x; lastEmit.y = y; }
        start();
        if (Math.hypot(x - lastEmit.x, y - lastEmit.y) > 12) {
          shed(x, y);
          lastEmit.x = x; lastEmit.y = y;
        }
        qx(x); qy(y);
        gsap.to(reveal, { opacity: 1, duration: 0.3, overwrite: 'auto' });
        gsap.to(pos, { d: size(), duration: 0.5, ease: 'power3.out', overwrite: 'auto' });
        gsap.to(ring, { opacity: 0.55, duration: 0.3, overwrite: 'auto' });
      });
      sec.addEventListener('pointerleave', () => {
        if (locked) return;
        spd = 0;
        gsap.to([reveal, ring], { opacity: 0, duration: 0.4, overwrite: 'auto' });
        gsap.to(pos, { d: 0, duration: 0.45, ease: 'power2.in', overwrite: 'auto' });
      });

      // fijar el paisaje, como el "tap to lock" de la referencia
      const lock = sec.querySelector('[data-hero-lock]');
      const label = sec.querySelector('[data-hero-lock-label]');
      if (lock) {
        lock.addEventListener('click', () => {
          locked = !locked;
          if (label) label.textContent = locked ? 'Volver al estudio' : 'Explora la aventura';
          if (locked) {
            start();
            gsap.to(reveal, { opacity: 1, duration: 0.6, overwrite: 'auto' });
            gsap.to(ring, { opacity: 0, duration: 0.3, overwrite: 'auto' });
          } else {
            parts.forEach((p) => { p.life = 0; });
            gsap.to(reveal, { opacity: 0, duration: 0.5, overwrite: 'auto' });
            gsap.to(pos, { d: 0, duration: 0.7, ease: 'power2.inOut', overwrite: 'auto' });
          }
        });
      }
    }

  }

  buildApertura() {
    const gsap = window.gsap;
    const sec = document.querySelector('[data-ch="apertura"]');
    if (!sec) return;
    const frames = gsap.utils.toArray(sec.querySelectorAll('[data-hframe]'));
    if (!frames.length) return;

    gsap.set(frames, { opacity: 0 });
    gsap.set(frames[0], { opacity: 1 });

    const wrap = sec.querySelector('[data-hframes]');
    const state = { i: 0 };
    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: sec, start: 'top top', end: '+=' + this.len(620),
        pin: true, scrub: 0.35, anticipatePin: 1, invalidateOnRefresh: true,
        onRefresh: () => {
          const sp = sec.parentElement;
          if (sp && sp.classList.contains('pin-spacer')) sp.style.pointerEvents = 'none';
        }
      }
    });

    // acercamiento continuo a la prenda
    if (wrap) {
      gsap.set(wrap, { transformOrigin: '50% 42%' });
      const apSpacer = sec.parentElement;
      if (apSpacer && apSpacer.classList.contains('pin-spacer')) apSpacer.style.pointerEvents = 'none';
      // se abre como una ventana al centro y se extiende a pantalla completa
      // la ventana sólo abre en el último tramo del hero
      const winST = {
        trigger: sec, start: 'top 62%', end: 'top top',
        scrub: 0.4, invalidateOnRefresh: true
      };
      gsap.fromTo(wrap,
        { clipPath: 'inset(50% 50% 50% 50%)' },
        { clipPath: 'inset(0% 0% 0% 0%)', ease: 'power2.inOut', scrollTrigger: winST });

      const apFrame = sec.querySelector('[data-ap-frame]');
      if (apFrame) {
        gsap.timeline({ scrollTrigger: winST })
          .fromTo(apFrame,
            { top: '50%', right: '50%', bottom: '50%', left: '50%', opacity: 0 },
            { top: '0%', right: '0%', bottom: '0%', left: '0%', opacity: 1, ease: 'power2.inOut', duration: 1 }, 0)
          .to(apFrame, { opacity: 0, ease: 'none', duration: 0.22 }, 0.78);
      }
      tl.fromTo(wrap, { scale: 1 }, { scale: 1.55, ease: 'power1.in', duration: 3 }, 0);
    }

    tl.to(state, {
      i: frames.length - 1, ease: 'none', duration: 3,
      onUpdate: () => {
        const n = Math.round(state.i);
        frames.forEach((f, k) => {
          const on = k === n;
          f.style.opacity = on ? '1' : '0';
        });
      }
    }, 0);
  }

  buildIntro() {
    const gsap = window.gsap;
    const sec = document.querySelector('[data-ch="intro"]');
    if (!sec) return;
    const panel = sec.querySelector('[data-intro-panel]');
    const quote = sec.querySelector('[data-intro-quote]');
    const art = sec.querySelector('[data-intro-art]');
    const qw = this.splitWords(quote).concat(this.splitWords(sec.querySelector('[data-intro-quote2]')));

    // la hoja de papel entra deslizándose sobre la mesa: continuidad con la secuencia anterior
    const row = sec.querySelector('[data-intro-row]');
    const seal = sec.querySelector('[data-intro-seal]');
    const qi = qw;
    if (panel) gsap.set(panel, { autoAlpha: 0, clearProps: 'transform' });
    if (row) gsap.set(row, { transformOrigin: '50% 50%' });
    gsap.set(qw, { yPercent: 0 });

    // la hoja se desliza desde abajo cubriendo el último cuadro de la mesa

    const iGrid = sec.querySelector('[data-intro-gridtex]');
    if (iGrid && this.props.parallax !== false) {
      gsap.fromTo(iGrid, { backgroundPositionY: '-140px' }, {
        backgroundPositionY: '140px', ease: 'none',
        scrollTrigger: { trigger: sec, start: 'top bottom', end: 'bottom top', scrub: 0.6 }
      });
    }

    if (art) {
      gsap.set(art, { opacity: 1, y: 0 });
      const sketch = art.querySelector('img');
      // flotación discreta: el boceto respira junto al texto
      if (sketch) {
        gsap.to(sketch, { y: -12, rotate: -1.2, duration: 3.2, ease: 'sine.inOut', repeat: -1, yoyo: true });
      }
    }

    // la foto se abre desde el borde y respira un acercamiento lento
    const photoImg = sec.querySelector('[data-intro-img]');
    if (photoImg) {
      gsap.fromTo(photoImg, { scale: 1.12 }, {
        scale: 1, ease: 'none', immediateRender: false,
        scrollTrigger: { trigger: sec, start: 'top bottom', end: 'bottom top', scrub: 0.8 }
      });
    }

    // la cita se queda anclada (pin real) mientras "Nueva etapa" entra de lado y sólo se suelta cuando
    // ya quedó cubierta: así se desplaza sin que se vea. El tramo extra (etapaCover) lo compensa
    // "Nueva etapa" con un margin-top más negativo, para que lo que sigue no se recorra.
    const introPx = () => Math.round(window.innerHeight * parseFloat(this.len(230)) / 100);
    const etapaSec = document.querySelector('[data-ch="etapa"]');
    this.syncEtapaOverlap = () => {
      if (etapaSec) etapaSec.style.marginTop = 'calc(-100vh - ' + this.etapaCover() + 'px)';
    };
    this.syncEtapaOverlap();
    window.ScrollTrigger.addEventListener('refreshInit', this.syncEtapaOverlap);
    window.ScrollTrigger.create({
      trigger: sec, start: 'top top', end: () => '+=' + (introPx() + this.etapaCover()),
      pin: true, anticipatePin: 1, invalidateOnRefresh: true,
      onEnter: () => { if (panel) gsap.set(panel, { autoAlpha: 1 }); },
      onEnterBack: () => { if (panel) gsap.set(panel, { autoAlpha: 1 }); },
      onLeaveBack: () => { if (panel) gsap.set(panel, { autoAlpha: 0 }); },
      onRefresh: (self) => { if (panel) gsap.set(panel, { autoAlpha: self.scroll() >= self.start ? 1 : 0 }); }
    });
    const tl = gsap.timeline({
      scrollTrigger: {
        id: 'csp26-intro', trigger: sec, start: 'top top', end: () => '+=' + introPx(),
        scrub: 0.7, invalidateOnRefresh: true
      }
    });
    if (panel) {
      tl.fromTo(panel, { yPercent: 100 }, { yPercent: 0, ease: 'power3.out', duration: 0.7 }, 0);
    }
    if (row) {
      tl.fromTo(row, { y: 40, opacity: 0 }, { y: 0, opacity: 1, ease: 'power2.out', duration: 0.5 }, 0.24);
    }
    const photo = sec.querySelector('[data-intro-photo]');
    if (photo) {
      tl.fromTo(photo, { clipPath: 'inset(0% 100% 0% 0%)' }, { clipPath: 'inset(0% 0% 0% 0%)', ease: 'power3.out', duration: 0.6 }, 0.2);
    }
    if (seal) {
      tl.fromTo(seal, { opacity: 0, scale: 0.5 }, { opacity: 1, scale: 1, ease: 'back.out(2)', duration: 0.4 }, 0.42);
    }
    if (qi.length) {
      tl.fromTo(qi, { yPercent: 115 }, { yPercent: 0, ease: 'power3.out', duration: 0.5, stagger: 0.05 }, 0.56);
    }
    if (art) {
      tl.fromTo(art, { opacity: 0, y: 44 }, { opacity: 1, y: 0, ease: 'power2.out', duration: 0.5 }, 0.86);
    }
    tl.to({}, { duration: 0.8 }, 1.2);
  }


  buildEstandar() {
    const gsap = window.gsap;
    const sec = document.querySelector('[data-ch="estandar"]');
    if (!sec) return;
    const grid = sec.querySelector('[data-est-gridtex]');
    const lines = gsap.utils.toArray(sec.querySelectorAll('[data-est-line]'));
    const words = lines.reduce((acc, el) => acc.concat(this.splitWords(el)), []);

    // la hoja sigue bajando: el grid se desplaza más lento que la sección
    if (grid && this.props.parallax !== false) {
      gsap.fromTo(grid, { yPercent: -9 }, {
        yPercent: 9, ease: 'none',
        scrollTrigger: { trigger: sec, start: 'top bottom', end: 'bottom top', scrub: 0.6 }
      });
    }

    const stage = sec.querySelector('[data-est-stage]');
    if (stage) {
      gsap.fromTo(stage,
        { filter: 'blur(14px)', scale: 1.22, opacity: 0.35 },
        {
          filter: 'blur(0px)', scale: 1, opacity: 1, ease: 'power2.out', immediateRender: false,
          scrollTrigger: {
            trigger: sec, start: 'top top', end: () => '+=' + Math.round(window.innerHeight * 0.5),
            scrub: 0.5, invalidateOnRefresh: true
          }
        });
    }

    // el componente pierde su observer al remontarse dentro del runtime: lo reactivamos a mano
    const flag = sec.querySelector('flag-wave');
    if (flag) {
      const wake = () => {
        flag._hidden = false;
        if (flag._io) { flag._io.disconnect(); flag._io = null; }
        if (flag._observe) flag._observe();
        flag._hidden = false;
        if (flag._start) flag._start();
      };
      if (window.customElements && customElements.whenDefined) {
        customElements.whenDefined('flag-wave').then(() => setTimeout(wake, 60));
      } else setTimeout(wake, 200);
      window.ScrollTrigger.create({ trigger: sec, start: 'top bottom', end: 'bottom top', onToggle: (self) => { if (self.isActive) wake(); } });
    }

    gsap.set(words, { yPercent: 118 });
    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: sec, start: 'top top', end: '+=' + this.len(170),
        pin: true, scrub: 0.7, anticipatePin: 1, invalidateOnRefresh: true
      }
    });
    // las letras suben como si se revelaran grabadas en el metal
    tl.to(words, { yPercent: 0, ease: 'power3.out', stagger: 0.07, duration: 0.8 }, 0.3);
    const sub = sec.querySelector('[data-est-sub]');
    if (sub) {
      gsap.set(sub, { opacity: 0, y: 22 });
      tl.to(sub, { opacity: 1, y: 0, ease: 'power2.out', duration: 0.7 }, 0.85);
    }
    tl.to({}, { duration: 0.7 }, 1.3);
  }

  buildDecisiones() {
    const gsap = window.gsap;
    const sec = document.querySelector('[data-ch="decisiones"]');
    if (!sec) return;
    const grid = sec.querySelector('[data-dec-gridtex]');
    const caps = gsap.utils.toArray(sec.querySelectorAll('[data-dec-cap]'));
    const shots = gsap.utils.toArray(sec.querySelectorAll('[data-dec-shot]'));
    if (!caps.length) return;

    if (grid && this.props.parallax !== false) {
      gsap.fromTo(grid, { backgroundPositionY: '-120px' }, {
        backgroundPositionY: '120px', ease: 'none',
        scrollTrigger: { trigger: sec, start: 'top bottom', end: 'bottom top', scrub: 0.6 }
      });
    }

    const quote = sec.querySelector('[data-dec-quote]');
    const seal = sec.querySelector('[data-dec-seal]');
    const qw = quote ? this.splitWords(quote) : [];
    if (qw.length) gsap.set(qw, { yPercent: 118 });
    if (seal) gsap.set(seal, { autoAlpha: 0, y: 18 });
    // primero se lee la cita; el primer par (texto + foto) entra después
    gsap.set(caps, { autoAlpha: 0, y: 26 });
    gsap.set(shots, { autoAlpha: 0 });
    if (shots[0]) gsap.set(shots[0], { autoAlpha: 1 });

    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: sec, start: 'top top', end: () => '+=' + window.innerHeight * 4.1,
        pin: true, scrub: 0.7, anticipatePin: 1, invalidateOnRefresh: true
      }
    });
    if (seal) tl.to(seal, { autoAlpha: 1, y: 0, ease: 'power2.out', duration: 0.5 }, 0);
    if (qw.length) tl.to(qw, { yPercent: 0, ease: 'power3.out', stagger: 0.06, duration: 0.8 }, 0.15);
    tl.to(caps[0], { autoAlpha: 1, y: 0, ease: 'power2.out', duration: 0.4 }, 1.15);
    tl.to({}, { duration: 0.9 }, 1.75);

    // cada paso de scroll sustituye el texto y su foto
    const step = 1;
    for (let i = 1; i < caps.length; i++) {
      const at = 2.65 + (i - 1) * step;
      tl.to(caps[i - 1], { autoAlpha: 0, y: -22, ease: 'power2.in', duration: 0.34 }, at);
      tl.fromTo(caps[i], { autoAlpha: 0, y: 26 }, { autoAlpha: 1, y: 0, ease: 'power2.out', duration: 0.4 }, at + 0.3);
      const prevShot = shots[i - 1], nextShot = shots[i];
      if (nextShot) {
        tl.to(prevShot, { autoAlpha: 0, ease: 'power1.inOut', duration: 0.5 }, at + 0.1);
        tl.fromTo(nextShot, { autoAlpha: 0, scale: 1.05 }, { autoAlpha: 1, scale: 1, ease: 'power1.out', duration: 0.6 }, at + 0.1);
      }
    }
    // en el último paso la cita se retira y la frase de cierre ocupa su lugar
    const lastAt = 2.65 + (caps.length - 2) * step;
    const capsBox = sec.querySelector('[data-dec-caps]');
    const col = sec.querySelector('[data-dec-col]');
    const finalCap = sec.querySelector('[data-dec-final]');
    const rise = () => {
      if (!capsBox || !col || !finalCap) return 0;
      const c = col.getBoundingClientRect(), b = capsBox.getBoundingClientRect();
      const h = finalCap.getBoundingClientRect().height;
      return (c.top + c.height / 2 - h / 2) - b.top;
    };
    if (seal) tl.to(seal, { autoAlpha: 0, y: -18, ease: 'power2.in', duration: 0.35 }, lastAt);
    if (quote) tl.to(quote, { autoAlpha: 0, y: -18, ease: 'power2.in', duration: 0.35 }, lastAt);
    if (capsBox) tl.to(capsBox, { y: rise, ease: 'power2.inOut', duration: 0.6 }, lastAt + 0.2);
    tl.to({}, { duration: 0.9 }, 1.8 + (caps.length - 1) * step);
  }

  buildSecuencia() {
    const gsap = window.gsap;
    const sec = document.querySelector('[data-ch="secuencia"]');
    if (!sec) return;
    const frames = gsap.utils.toArray(sec.querySelectorAll('[data-frame]'));
    const copy = sec.querySelector('[data-seq-copy]');
    const fill = sec.querySelector('[data-seq-fill]');
    if (!frames.length) return;

    const cw = copy ? this.splitWords(copy) : [];
    gsap.set(frames, { opacity: 0 });
    gsap.set(frames[0], { opacity: 1 });
    if (cw.length) gsap.set(cw, { yPercent: 122 });

    // un cuadro por tramo de scroll: el usuario "pasa" la película
    const state = { i: 0 };
    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: sec, start: 'top top', end: '+=' + this.len(430),
        pin: true, scrub: 0.35, anticipatePin: 1, invalidateOnRefresh: true
      }
    });

    tl.to(state, {
      i: frames.length - 1, ease: 'none', duration: 3,
      onUpdate: () => {
        const n = Math.round(state.i);
        frames.forEach((f, k) => {
          const on = k === n;
          f.style.opacity = on ? '1' : '0';
        });
      }
    }, 0);
    if (cw.length) tl.to(cw, { yPercent: 0, ease: 'power3.out', stagger: 0.05, duration: 0.8 }, 0.3);

    // el último cuadro se acerca y se funde en la hoja de papel
    const paper = sec.querySelector('[data-seq-paper]');
    const fade = [copy, sec.querySelector('[data-seq-meter]')].filter(Boolean);
    const wrap = sec.querySelector('[data-frames]');
    if (wrap) tl.fromTo(wrap, { scale: 1 }, { scale: 1.12, ease: 'power1.in', duration: 0.6 }, 2.4);
    if (fade.length) tl.to(fade, { opacity: 0, ease: 'power2.in', duration: 0.35 }, 2.5);
    if (paper) tl.fromTo(paper, { opacity: 0 }, { opacity: 1, ease: 'power2.inOut', duration: 0.45 }, 2.6);
  }

  // tramo de scroll en el que "Nueva etapa" entra y termina de cubrir la cita (hasta 2.3 de 5.7 de su
  // timeline: entrada de 1.7 + la pausa de 0.6, que absorbe el retraso del scrub)
  etapaCover() {
    const sec = document.querySelector('[data-ch="etapa"]');
    const viewport = sec && sec.querySelector('[data-eh-viewport]');
    const track = sec && sec.querySelector('[data-eh-track]');
    if (!viewport || !track) return 0;
    const dist = Math.max(0, track.scrollWidth - viewport.getBoundingClientRect().width);
    return Math.round((dist + window.innerHeight * 2.2) * 2.3 / 5.7);
  }

  buildEtapa() {
    const gsap = window.gsap;
    const sec = document.querySelector('[data-ch="etapa"]');
    if (!sec) return;
    const panel = sec.querySelector('[data-etapa-panel]');
    const viewport = sec.querySelector('[data-eh-viewport]');
    const track = sec.querySelector('[data-eh-track]');
    if (!viewport || !track) return;
    const grid = sec.querySelector('[data-etapa-gridtex]');
    const prev = document.querySelector('[data-intro-panel]');
    const dist = () => Math.max(0, track.scrollWidth - viewport.getBoundingClientRect().width);

    if (grid && this.props.parallax !== false) {
      gsap.fromTo(grid, { backgroundPositionY: '-120px' }, {
        backgroundPositionY: '120px', ease: 'none',
        scrollTrigger: { trigger: sec, start: 'top bottom', end: 'bottom top', scrub: 0.6 }
      });
    }

    if (panel) gsap.set(panel, { autoAlpha: 0 });
    // la cita sigue anclada por su propio pin (ver buildIntro); aquí sólo se empuja hacia la izquierda
    const releasePrev = () => { if (prev) gsap.set(prev, { x: 0 }); };
    const pushPrev = () => {
      if (!prev) return;
      const left = panel ? panel.getBoundingClientRect().left : 0;
      gsap.set(prev, { x: -(window.innerWidth - left) * 0.16 });
    };
    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: sec, start: 'top top', end: () => '+=' + (dist() + window.innerHeight * 2.2),
        pin: true, scrub: 0.7, anticipatePin: 1, invalidateOnRefresh: true,
        onEnter: () => { if (panel) gsap.set(panel, { autoAlpha: 1 }); },
        onEnterBack: () => { if (panel) gsap.set(panel, { autoAlpha: 1 }); },
        onUpdate: pushPrev,
        onLeave: releasePrev,
        onLeaveBack: () => { if (panel) gsap.set(panel, { autoAlpha: 0 }); releasePrev(); },
        onRefresh: (self) => { if (panel) gsap.set(panel, { autoAlpha: self.scroll() >= self.start ? 1 : 0 }); }
      }
    });
    // la sección entra de lado y empuja la anterior hacia la izquierda
    if (panel) {
      tl.fromTo(panel, { xPercent: 100 }, { xPercent: 0, ease: 'power1.out', duration: 1.7 }, 0);
    }
    // la cita se lee primero y luego desfila junto con las fotos
    tl.to({}, { duration: 0.6 }, 1.7);
    tl.to(track, { x: () => -dist(), ease: 'none', duration: 3.4 }, 2.3);
  }

  buildVuelta() {
    const gsap = window.gsap;
    const sec = document.querySelector('[data-ch="vuelta"]');
    if (!sec) return;
    const panel = sec.querySelector('[data-vuelta-panel]');
    const title = sec.querySelector('[data-sec-title]');
    const rule = sec.querySelector('[data-vuelta-rule]');
    const lede = sec.querySelector('[data-lede]');
    const scroller = sec.querySelector('[data-h-scroll]');
    const track = sec.querySelector('[data-h-track]');
    const cards = gsap.utils.toArray(track.children);
    const cue = this.cue(sec);
    const tw = this.splitWords(title);
    cards.forEach((c) => { c.style.scrollSnapAlign = 'start'; });
    gsap.set(track, { x: 0 });

    // la sección entra como un acercamiento desde el centro: nada se desplaza
    const row = sec.querySelector('[data-vuelta-row]');
    gsap.set(tw, { yPercent: 0, opacity: 1, scale: 1 });
    gsap.set(rule, { scaleX: 1, transformOrigin: '50% 50%' });
    gsap.set(lede, { opacity: 1, scale: 1 });
    gsap.set(cards, { opacity: 1, scale: 1, clearProps: 'clipPath' });
    if (row) gsap.set(row, { transformOrigin: '50% 50%' });
    if (cue) gsap.set(cue, { opacity: 0 });

    if (panel) gsap.set(panel, { autoAlpha: 0 });
    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: sec, start: 'top top', end: '+=' + this.len(130),
        pin: true, scrub: 0.7, anticipatePin: 1, invalidateOnRefresh: true,
        onEnter: () => { if (panel) gsap.set(panel, { autoAlpha: 1 }); },
        onEnterBack: () => { if (panel) gsap.set(panel, { autoAlpha: 1 }); },
        onLeaveBack: () => { if (panel) gsap.set(panel, { autoAlpha: 0 }); },
        onRefresh: (self) => { if (panel) gsap.set(panel, { autoAlpha: self.scroll() >= self.start ? 1 : 0 }); }
      }
    });
    if (row) {
      tl.fromTo(row, { scale: 0.62, opacity: 0 }, { scale: 1, opacity: 1, ease: 'power2.out', duration: 1 }, 0);
    }
    // la colección la recorre el usuario: el riel es un carrusel libre
    tl.to({}, { duration: 0.6 }, 1);
    if (cue) tl.to(cue, { opacity: 1, duration: 0.4 }, 1.2);
    this.syncTrackBar('[data-h-track]', '[data-h-thumb]', scroller);
  }

  buildExigencia() {
    const gsap = window.gsap;
    const sec = document.querySelector('[data-ch="exigencia"]');
    if (!sec) return;
    const title = sec.querySelector('[data-sec-title]');
    const rule = sec.querySelector('[data-exi-rule]');
    const lede = gsap.utils.toArray(sec.querySelectorAll('[data-lede]'));
    const layers = gsap.utils.toArray(sec.querySelectorAll('[data-layer]'));
    const cue = this.cue(sec);
    const tw = this.splitWords(title);

    gsap.set(tw, { yPercent: 120 });
    gsap.set(rule, { scaleX: 0 });
    gsap.set(lede, { opacity: 0, y: 22 });
    // cada prenda se descubre completa antes de que entre la siguiente
    gsap.set(layers, { clipPath: 'inset(100% 0 0 0)', y: 26, opacity: 1 });
    if (cue) gsap.set(cue, { opacity: 0 });

    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: sec, start: 'top top', end: '+=' + this.len(330),
        pin: true, scrub: 0.7, anticipatePin: 1, invalidateOnRefresh: true
      }
    });
    tl.to(tw, { yPercent: 0, ease: 'power3.out', stagger: 0.05, duration: 0.6 }, 0)
      .to(rule, { scaleX: 1, ease: 'power2.out', duration: 0.7 }, 0.3)
      .to(lede, { opacity: 1, y: 0, ease: 'power2.out', stagger: 0.08, duration: 0.6 }, 0.45);

    const compact = window.matchMedia('(max-width:1000px)').matches;
    if (compact) {
      // en el carrusel las tres aparecen juntas, sin escalera
      tl.to(layers, { clipPath: 'inset(0% 0 0 0)', y: 0, ease: 'power2.out', duration: 0.7 }, 0.7);
    } else {
      layers.forEach((el, i) => {
        tl.to(el, { clipPath: 'inset(0% 0 0 0)', y: 0, ease: 'power3.out', duration: 0.9 }, 0.8 + i * 0.7);
      });
    }
    // deriva escalonada: sólo en desktop, donde existe la escalera
    if (!compact) {
      layers.forEach((el, i) => {
        tl.to(el, { y: [-22, 16, -12][i] || 0, ease: 'none', duration: 2 }, 2.9);
      });
    }
    if (cue) tl.to(cue, { opacity: 1, duration: 0.4 }, 3.6);
  }

  syncTrackBar(trackSel, thumbSel, scrollerEl) {
    const el = document.querySelector(trackSel);
    const thumb = document.querySelector(thumbSel);
    if (!el || !thumb) return;
    const track = scrollerEl || el;
    const n = el.children.length || 1;
    const update = () => {
      const max = track.scrollWidth - track.clientWidth;
      const p = max > 0 ? track.scrollLeft / max : 0;
      thumb.style.width = (100 / n) + '%';
      thumb.style.transform = 'translateX(' + (p * (n - 1) * 100) + '%)';
    };
    track.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    update();
  }

  syncLayersBar() {
    const track = document.querySelector('[data-layers]');
    const thumb = document.querySelector('[data-layers-thumb]');
    if (!track || !thumb) return;
    const n = track.children.length || 1;
    const update = () => {
      const max = track.scrollWidth - track.clientWidth;
      const p = max > 0 ? track.scrollLeft / max : 0;
      thumb.style.width = (100 / n) + '%';
      thumb.style.transform = 'translateX(' + (p * (n - 1) * 100) + '%)';
    };
    track.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    update();
  }

  buildKids() {
    const gsap = window.gsap;
    const sec = document.querySelector('[data-ch="kids"]');
    if (!sec) return;
    const panel = sec.querySelector('[data-kids-panel]');
    const title = sec.querySelector('[data-kids-title]');
    const quote = sec.querySelector('[data-kids-quote]');
    const seal = sec.querySelector('[data-kids-seal]');
    const photo = sec.querySelector('[data-kids-photo]');
    const kRule = sec.querySelector('[data-kids-rule]');
    const cue = this.cue(sec);
    const tw = this.splitWords(title);

    if (panel) {
      gsap.fromTo(panel, { yPercent: 24 }, {
        yPercent: 0, ease: 'none', immediateRender: false,
        onUpdate: function () {
          const q = 1 - this.progress();
          panel.style.borderRadius = (50 * q) + '% ' + (50 * q) + '% 0 0 / ' + (13 * q) + 'vh ' + (13 * q) + 'vh 0 0';
        },
        scrollTrigger: { trigger: sec, start: 'top bottom', end: 'top top', scrub: 0.5 }
      });
    }

    gsap.set(tw, { yPercent: 118 });
    if (kRule) gsap.set(kRule, { scaleX: 0 });
    gsap.set(quote, { opacity: 0, y: 22 });
    if (seal) gsap.set(seal, { opacity: 0, y: 18 });
    if (photo) gsap.set(photo, { clipPath: 'inset(0 0 100% 0)' });
    if (cue) gsap.set(cue, { opacity: 0 });

    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: sec, start: 'top top', end: '+=' + this.len(200),
        pin: true, scrub: 0.7, anticipatePin: 1, invalidateOnRefresh: true
      }
    });
    tl.to(tw, { yPercent: 0, ease: 'power3.out', stagger: 0.05, duration: 0.7 }, 0)
      .to(kRule || {}, { scaleX: 1, ease: 'power2.out', duration: 0.6 }, 0.45)
      .to(quote, { opacity: 1, y: 0, ease: 'power2.out', duration: 0.6 }, 0.75);
    if (seal) tl.to(seal, { opacity: 1, y: 0, ease: 'power2.out', duration: 0.5 }, 0.6);
    if (photo) tl.to(photo, { clipPath: 'inset(0 0 0% 0)', ease: 'power3.out', duration: 0.9 }, 0.2);
    if (cue) tl.to(cue, { opacity: 1, duration: 0.4 }, 1.3);
    tl.to({}, { duration: 0.8 }, 1.4);
  }

  buildKidsLinea() {
    const gsap = window.gsap;
    const sec = document.querySelector('[data-ch="kidslinea"]');
    if (!sec) return;
    const photo = sec.querySelector('[data-kl-photo]');
    const lede = sec.querySelector('[data-kl-lede]');
    const title = sec.querySelector('[data-kl-title]');
    const rule = sec.querySelector('[data-kl-rule]');
    const more = sec.querySelector('[data-kl-more]');
    const kids = gsap.utils.toArray(sec.querySelectorAll('[data-kid]'));

    if (photo) gsap.set(photo, { clipPath: 'inset(0 0 100% 0)' });
    gsap.set([title, lede], { opacity: 0, y: 22 });
    if (rule) gsap.set(rule, { scaleX: 0 });
    gsap.set(kids, { clipPath: 'inset(100% 0 0 0)', y: 24 });
    if (more) gsap.set(more, { opacity: 0, y: 18 });

    // Gorras entra con margin-top:-100vh y cubre los últimos 100vh del pin. Las tarjetas
    // terminan de revelarse a la mitad del tramo libre (antes de que empiece a cubrir) y el
    // resto queda quieto, para que la última tarjeta se vea completa antes de la cortina.
    const pinLen = 380 * (this.props.scrollLength || 1);
    const free = Math.max(0.3, (pinLen - 100) / pinLen);
    const revealEnd = 1 + (kids.length - 1) * 0.22 + 0.7;
    const total = revealEnd / (free * 0.55);

    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: sec, start: 'top top', end: '+=' + this.len(380),
        pin: true, scrub: 0.7, anticipatePin: 1, invalidateOnRefresh: true
      }
    });
    if (photo) tl.to(photo, { clipPath: 'inset(0 0 0% 0)', ease: 'power3.out', duration: 0.9 }, 0);
    tl.to(title, { opacity: 1, y: 0, ease: 'power2.out', duration: 0.5 }, 0.25)
      .to(rule || {}, { scaleX: 1, ease: 'power2.out', duration: 0.5 }, 0.45)
      .to(lede, { opacity: 1, y: 0, ease: 'power2.out', duration: 0.5 }, 0.55);
    if (more) tl.to(more, { opacity: 1, y: 0, ease: 'power2.out', duration: 0.5 }, 0.7);
    kids.forEach((el, i) => {
      tl.to(el, { clipPath: 'inset(0% 0 0 0)', y: 0, ease: 'power3.out', duration: 0.7 }, 1 + i * 0.22);
    });
    tl.to({}, { duration: Math.max(0.1, total - revealEnd) }, revealEnd);
  }

  buildPasion() {
    const gsap = window.gsap;
    const sec = document.querySelector('[data-ch="pasion"]');
    if (!sec) return;
    const title = sec.querySelector('[data-sec-title]');
    const lede = sec.querySelector('[data-lede]');
    const pRule = sec.querySelector('[data-pas-rule]');
    const caps = gsap.utils.toArray(sec.querySelectorAll('[data-cap]'));
    const cue = this.cue(sec);
    const tw = this.splitWords(title);

    gsap.set(tw, { yPercent: 120 });
    gsap.set(lede, { opacity: 0, y: 22 });
    if (pRule) gsap.set(pRule, { scaleX: 0 });
    gsap.set(caps, { y: 40, opacity: 0 });
    if (cue && window.innerHeight < 720) gsap.set(cue, { display: 'none' });
    // el panel viaja con la sección: cubre a kids, que permanece anclada

    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: sec, start: 'top top', end: '+=' + this.len(240),
        pin: true, scrub: 0.7, anticipatePin: 1, invalidateOnRefresh: true
      }
    });
    tl.to(tw, { yPercent: 0, ease: 'power3.out', stagger: 0.05, duration: 0.6 }, 0)
      .to(pRule || {}, { scaleX: 1, ease: 'power2.out', duration: 0.7 }, 0.28)
      .to(lede, { opacity: 1, y: 0, ease: 'power2.out', duration: 0.6 }, 0.45)
      // el panel ya hizo la entrada: aquí sólo se asientan
      .to(caps, { y: 0, opacity: 1, ease: 'power3.out', stagger: 0.12, duration: 0.9 }, 0.7);
    if (cue) {
      gsap.set(cue, { opacity: 0 });
      tl.to(cue, { opacity: 1, duration: 0.4 }, 2.8);
    }
  }

  buildExtra() {
    const gsap = window.gsap;
    const sec = document.querySelector('[data-ch="extra"]');
    if (!sec) return;
    const title = sec.querySelector('[data-extra-title]');
    const rule = sec.querySelector('[data-extra-rule]');
    const lines = gsap.utils.toArray(sec.querySelectorAll('[data-extra-line]'));
    const shot = sec.querySelector('[data-extra-shot]');
    const badge = sec.querySelector('[data-extra-badge]');
    const cue = this.cue(sec);
    const tw = this.splitWords(title);

    gsap.set(tw, { yPercent: 120 });
    gsap.set(rule, { scaleX: 0 });
    gsap.set(lines, { opacity: 0, y: 24 });
    if (shot) gsap.set(shot, { clipPath: 'inset(0 100% 0 0)' });
    if (badge) gsap.set(badge, { opacity: 0, scale: 0.6, rotate: -18, transformOrigin: '100% 0%' });
    if (cue) gsap.set(cue, { opacity: 0 });

    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: sec, start: 'top top', end: '+=' + this.len(280),
        pin: true, scrub: 0.7, anticipatePin: 1, invalidateOnRefresh: true
      }
    });

    tl.to(tw, { yPercent: 0, ease: 'power3.out', stagger: 0.07, duration: 0.8 }, 0);
    if (shot) tl.to(shot, { clipPath: 'inset(0 0% 0 0)', ease: 'power3.inOut', duration: 1.1 }, 0.15);
    if (badge) tl.to(badge, { opacity: 1, scale: 1, rotate: -3, ease: 'back.out(2.2)', duration: 0.7 }, 1);
    tl.to(rule, { scaleX: 1, ease: 'power2.out', duration: 0.7 }, 0.7)
      .to(lines, { opacity: 1, y: 0, ease: 'power2.out', stagger: 0.2, duration: 0.7 }, 0.6);
    if (cue) tl.to(cue, { opacity: 1, duration: 0.4 }, 2.2);
  }

  buildCierre() {
    const gsap = window.gsap;
    const sec = document.querySelector('[data-ch="cierre"]');
    if (!sec) return;
    const glow = sec.querySelector('[data-fin-glow]');
    const logo = sec.querySelector('[data-fin-logo]');
    const title = sec.querySelector('[data-fin-title]');
    const quotes = gsap.utils.toArray(sec.querySelectorAll('[data-fin-quote]'));
    const tail = sec.querySelector('[data-fin-tail]');
    const sign = sec.querySelector('[data-fin-sign]');
    const fade = sec.querySelector('[data-fin-fade]');
    if (fade) {
      gsap.fromTo(fade, { opacity: 1 }, { opacity: 0.25, ease: 'none',
        scrollTrigger: { trigger: sec, start: 'top bottom', end: 'top 20%', scrub: 0.6 } });
    }
    const bg = sec.querySelector('[data-fin-bg]');
    if (bg) {
      gsap.fromTo(bg, { scale: 1.08 }, { scale: 1, ease: 'none',
        scrollTrigger: { trigger: sec, start: 'top bottom', end: 'bottom bottom', scrub: 0.6 } });
    }

    const tw = this.splitWords(title);
    const qw = quotes.reduce((acc, q) => acc.concat(this.splitWords(q)), []);
    const tlw = this.splitWords(tail);

    gsap.set(glow, { opacity: 1 });
    gsap.set(logo, { opacity: 0, y: 14, scale: 0.82 });
    gsap.set(tw, { yPercent: 122 });
    gsap.set(qw, { yPercent: 122 });
    if (tlw.length) gsap.set(tlw, { yPercent: 122 });
    gsap.set(sign, { clipPath: 'inset(0 100% 0 0)' });


    gsap.timeline({
      defaults: { ease: 'power3.out' },
      scrollTrigger: { trigger: sec, start: 'top 58%', once: true }
    })
      .to(logo, { opacity: 1, y: 0, scale: 1, duration: 0.7 }, 0.1)
      .to(tw, { yPercent: 0, stagger: 0.07, duration: 0.9 }, 0.25)
      .to(qw, { yPercent: 0, stagger: 0.045, duration: 0.85 }, 0.7)
      .to(tlw.length ? tlw : {}, { yPercent: 0, stagger: 0.045, duration: 0.85 }, 1.35)
      .to(sign, { clipPath: 'inset(0 0% 0 0)', duration: 1.5, ease: 'power2.inOut' }, 2.05)
      .to({}, { duration: 0.6 }, 3.05);
  }

  buildNav(ST) {
    // fracción del recorrido en la que cada capítulo ya tiene todo revelado
    // vuelta: el acercamiento de la fila termina en 1/1.6 del timeline (0.625)
    const settled = { intro: 0.85, vuelta: 0.66 };
    const landing = (k) => {
      const sec = document.querySelector('[data-ch="' + k + '"]');
      if (!sec) return 0;
      const pinned = ST.getById('csp26-' + k) || ST.getAll().find((t) => t.trigger === sec && t.pin);
      if (!pinned) return sec.getBoundingClientRect().top + window.scrollY;
      return pinned.start + (pinned.end - pinned.start) * (settled[k] || 0);
    };
    const goTo = (k) => {
      const y = landing(k);
      if (this.lenis) this.lenis.scrollTo(y, { immediate: true, force: true });
      else window.scrollTo(0, y);
      // sin esto el scrub (0.7s) reproduce la animación a la vista al aterrizar
      ST.update();
      ST.getAll().forEach((t) => { const tw = t.getTween && t.getTween(); if (tw) tw.progress(1); });
    };
    this.goTo = goTo;
    [['productos', 'vuelta'], ['historia', 'intro']].forEach(([id, k]) => {
      const el = document.querySelector('[data-hero-cta="' + id + '"]');
      if (el) el.addEventListener('click', (e) => { e.preventDefault(); goTo(k); });
    });

    const top = document.querySelector('[data-top]');
    if (top) {
      const gsap = window.gsap;
      top.addEventListener('click', () => {
        if (this.lenis) this.lenis.scrollTo(0, { duration: 1.6 });
        else window.scrollTo({ top: 0, behavior: 'smooth' });
      });
      gsap.set(top, { autoAlpha: 0, y: 14 });
      // sólo al final del recorrido
      ST.create({
        trigger: document.querySelector('[data-ch="cierre"]'), start: 'top 55%',
        onEnter: () => gsap.to(top, { autoAlpha: 1, y: 0, duration: 0.4, overwrite: true }),
        onLeaveBack: () => gsap.to(top, { autoAlpha: 0, y: 14, duration: 0.3, overwrite: true })
      });
    }

    const jump = document.querySelector('[data-jump]');
    if (jump) {
      const gsap = window.gsap;
      const from = document.querySelector('[data-ch="apertura"]');
      const to = document.querySelector('[data-ch="secuencia"]');
      gsap.set(jump, { autoAlpha: 0, y: -10 });
      jump.addEventListener('click', (e) => {
        e.preventDefault();
        goTo('vuelta');
      });
      const show = (on) => gsap.to(jump, { autoAlpha: on ? 1 : 0, y: on ? 0 : -10, duration: 0.45, overwrite: true });
      if (from) {
        ST.create({
          trigger: from, start: 'top 70%',
          onEnter: () => show(true), onLeaveBack: () => show(false)
        });
      }
      const after = document.querySelector('#productos');
      if (after) {
        // el atajo se retira cuando la secuencia ya terminó su recorrido
        ST.create({
          trigger: after, start: 'top 90%',
          onEnter: () => show(false), onLeaveBack: () => show(true)
        });
      }
    }

    const down = document.querySelector('[data-down]');
    if (down) {
      const gsap = window.gsap;
      down.addEventListener('click', () => {
        const y = window.scrollY + window.innerHeight;
        if (this.lenis) this.lenis.scrollTo(y, { duration: 1.1 });
        else window.scrollTo({ top: y, behavior: 'smooth' });
      });
      // acompaña todo el recorrido y cede el lugar al botón de volver arriba
      ST.create({
        trigger: document.querySelector('[data-ch="cierre"]'), start: 'top 55%',
        onEnter: () => gsap.to(down, { autoAlpha: 0, y: 14, duration: 0.3, overwrite: true }),
        onLeaveBack: () => gsap.to(down, { autoAlpha: 1, y: 0, duration: 0.4, overwrite: true })
      });
    }
  }

  buildTicker(ST) {
    const gsap = window.gsap;
    const els = gsap.utils.toArray('[data-ticker]');
    if (!els.length) return;
    const loops = els.map((el) => {
      el.style.animation = 'none';
      return gsap.to(el, { xPercent: -50, duration: 34, ease: 'none', repeat: -1 });
    });
    ST.create({
      start: 0, end: 'max',
      onUpdate: (self) => {
        const v = self.getVelocity();
        const boost = gsap.utils.clamp(1, 6, 1 + Math.abs(v) / 700);
        loops.forEach((l) => gsap.to(l, { timeScale: v < 0 ? -boost : boost, duration: 0.35, overwrite: true }));
        clearTimeout(this.tickTO);
        this.tickTO = setTimeout(() => {
          loops.forEach((l) => gsap.to(l, { timeScale: v < 0 ? -1 : 1, duration: 0.8, overwrite: true }));
        }, 200);
      }
    });
  }

  splitWords(root) {
    if (!root) return [];
    const pieces = [];
    const glue = [];
    const walk = (node, css) => {
      Array.from(node.childNodes).forEach((n) => {
        const tight = n.nodeType === 1 && /^[“”"']+$/.test((n.textContent || '').trim());
        if (tight) glue.push(pieces.length);
        if (n.nodeType === 3) {
          n.textContent.split(/\s+/).forEach((t) => {
            if (!t) return;
            const mask = document.createElement('span');
            mask.style.cssText = 'display:inline-block;overflow:hidden;vertical-align:bottom;padding-bottom:.14em;margin-bottom:-.14em';
            const inner = document.createElement('span');
            inner.style.cssText = 'display:inline-block;will-change:transform;' + css;
            inner.textContent = t;
            mask.appendChild(inner);
            pieces.push(mask);
          });
        } else if (n.nodeType === 1) {
          walk(n, n.getAttribute('style') || '');
        }
      });
    };
    walk(root, '');
    root.textContent = '';
    const punct = /^[,.;:!?…”"'»)\]]+$/;
    pieces.forEach((m, i) => {
      root.appendChild(m);
      const next = pieces[i + 1];
      const noGap = glue.indexOf(i) >= 0 || glue.indexOf(i + 1) >= 0 ||
        (next && punct.test((next.textContent || '').trim()));
      if (i < pieces.length - 1 && !noGap) root.appendChild(document.createTextNode(' '));
    });
    return pieces.map((m) => m.firstChild);
  }

}

/* ——— arranque ——— */
(function () {
  window.__resources = window.__resources || {};
  var boot = function () {
    if (!document.querySelector('[data-ch="hero"]')) return;
    var app = new CSP26Landing(window.CSP26_CONFIG || {});
    window.CSP26 = app;
    app.start();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();

/* ——— editor de temas de Shopify: reinicia la coreografía al recargar la sección ——— */
document.addEventListener('shopify:section:load', function (e) {
  if (!e.target.querySelector('[data-ch="hero"]')) return;
  if (window.CSP26 && window.CSP26.destroy) { try { window.CSP26.destroy(); } catch (err) {} }
  var app = new CSP26Landing(window.CSP26_CONFIG || {});
  window.CSP26 = app;
  app.start();
});
